import { Badge } from "../../Badge";
import { Icon, type IconName } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps, CellEmphasis } from "../DataTable.types";
import { getCellContainerClasses, getCellTextClasses, getSkeletonSize } from "./cellUtils";

export interface BooleanCellProps<T = any> extends CellComponentProps<T> {
  format?: "icon" | "text" | "badge";
  trueText?: string;
  falseText?: string;
  trueIcon?: IconName;
  falseIcon?: IconName;
  showFalse?: boolean; // Whether to show false values or leave empty
  align?: "left" | "center" | "right";
  emphasis?: CellEmphasis;
  className?: string;
}

/**
 * BooleanCell
 *
 * DataTable cell component for displaying boolean values.
 * Supports multiple display formats including icons, text, or badges with customizable true/false representations.
 */
export function BooleanCell<T = any>({
  value,
  context,
  format = "icon",
  trueText = "Yes",
  falseText = "No",
  trueIcon = "Check",
  falseIcon = "X",
  showFalse = true,
  align = "center",
  emphasis = "normal",
  className,
}: BooleanCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  const boolValue = Boolean(value);

  if (!boolValue && !showFalse) {
    return <div className={getCellContainerClasses(align, className)} />;
  }

  const containerClassName = getCellContainerClasses(align, className);

  switch (format) {
    case "icon":
      return (
        <div className={containerClassName}>
          <Icon
            name={boolValue ? trueIcon : falseIcon}
            size={16}
            className={boolValue ? "text-green-600" : "text-gray-400"}
          />
        </div>
      );

    case "text":
      return (
        <div className={containerClassName}>
          <span className={getCellTextClasses(emphasis)}>{boolValue ? trueText : falseText}</span>
        </div>
      );

    case "badge":
      return (
        <div className={containerClassName}>
          <Badge variant={boolValue ? "success" : "neutral"} size="sm">
            {boolValue ? trueText : falseText}
          </Badge>
        </div>
      );

    default:
      return null;
  }
}
